import React, { FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import Api from 'Vendor/Api'
import {
    AiChoices,
    AiSuggestion,
    AiSuggestionKind,
    AiSuggestionStatus,
} from 'Vendor/UploadSystem'

type Props = {
    fileName: string
    suggestions?: AiSuggestion[]
    aiChoices: AiChoices
    updateAiSuggestion: (
        fileName: string,
        kind: AiSuggestionKind,
        payload: { status?: AiSuggestionStatus; value?: string; is_missing?: boolean; create_choice?: boolean },
    ) => Promise<void>
    isProcessing?: boolean
}

const _AiSuggestions: FC<Props> = ({
    fileName,
    suggestions,
    aiChoices,
    updateAiSuggestion,
    isProcessing = false,
}) => {
    const { t } = useTranslation()
    const [pendingKind, setPendingKind] = useState<AiSuggestionKind | null>(null)

    // Feature off for this org/bucket → render nothing.
    if (!aiChoices) {
        return null
    }

    const kinds = Object.keys(aiChoices).filter((kind) => aiChoices[kind])

    if (kinds.length === 0) {
        return null
    }

    const call = (
        kind: AiSuggestionKind,
        payload: { status?: AiSuggestionStatus; value?: string; propertyId?: number; is_missing?: boolean; create_choice?: boolean },
    ) => {
        setPendingKind(kind)

        const promise = (payload.create_choice && payload.propertyId && payload.value)
            ? Api.post(`/property/${payload.propertyId}/choice/new`, {
                value: payload.value,
                translations: {
                    fr: { label: payload.value },
                    en: { label: payload.value },
                },
            }).then(() =>
                Api.post('/ai/suggestion/status', {
                    propertyId: payload.propertyId,
                    value: payload.value,
                    status: 'accepted',
                })
            )
            : Promise.resolve()

        promise
            .then(() => updateAiSuggestion(fileName, kind, payload))
            .finally(() => setPendingKind(null))
    }

    return (
        <div className="ai-suggestions border-top pt-3 mt-3 text-start">
            <div className="ai-suggestions-title mb-3 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: '0.9rem' }}>✦</span>
                    <span>{t('app.ai_suggestions.title', 'Suggestions IA')}</span>
                </div>
                {isProcessing && (
                    <span className="ai-processing-badge">
                        <i className="fa-solid fa-wand-magic-sparkles fa-spin"></i>
                        <span>{t('app.ai_suggestions.processing', 'Analyse en cours...')}</span>
                    </span>
                )}
            </div>

            {kinds.map((kind) => {
                const property = aiChoices[kind]!
                const activeList = (suggestions ?? []).filter(
                    (s) => (s.kind === kind || s.property === kind) && s.status !== 'rejected',
                )
                const isPending = pendingKind === kind
                const count = activeList.length
                const countSuffix = count > 0 ? ` (${count})` : ''

                return (
                    <div key={kind} className="ai-suggestion-row mb-3">
                        <div className="ai-property-label">
                            {property.label}{countSuffix}
                        </div>

                        {activeList.length > 0 ? (
                            <div className="ai-chips-wrap">
                                {activeList.map((active, idx) => {
                                    const isMissing = active.is_missing ?? false
                                    const isAccepted = active.status === 'accepted'

                                    return (
                                        <div
                                            key={`${active.value}-${idx}`}
                                            className={`ai-chip ${isMissing ? 'ai-chip-missing border border-warning' : ''}`}
                                        >
                                            <span className="ai-chip-text">
                                                {isMissing && (
                                                    <span className="ai-missing-badge me-1" title="Nouvelle valeur suggérée (Création dans le DAM à la validation)">
                                                        ✨
                                                    </span>
                                                )}
                                                {active.value}
                                            </span>

                                            {isMissing && !isAccepted && (
                                                <button
                                                    type="button"
                                                    className="ai-accept-btn btn-sm ms-1"
                                                    title="Créer cette nouvelle valeur dans le DAM à la validation"
                                                    disabled={isPending}
                                                    onClick={() => call(kind, { status: 'accepted', value: active.value, propertyId: active.propertyId, is_missing: false, create_choice: true })}
                                                >
                                                    + ajouter
                                                </button>
                                            )}

                                            {isMissing && isAccepted && (
                                                <span className="ai-status-badge text-success font-weight-bold ms-1" title="Création validée">
                                                    ✓
                                                </span>
                                            )}

                                            <button
                                                type="button"
                                                className="ai-remove-btn"
                                                aria-label="Remove"
                                                disabled={isPending}
                                                onClick={() => call(kind, { status: 'rejected', value: active.value })}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : isProcessing ? (
                            <div className="ai-processing-row">
                                <div className="spinner-border spinner-border-sm text-secondary" role="status" style={{ width: '11px', height: '11px', borderWidth: '1.5px' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <span>{t('app.ai_suggestions.analyzing', 'Traitement...')}</span>
                            </div>
                        ) : (
                            <Form.Select
                                size="sm"
                                className="ai-select-control"
                                value=""
                                disabled={isPending}
                                onChange={(e) =>
                                    e.target.value &&
                                    call(kind, {
                                        value: e.target.value,
                                        status: 'accepted',
                                    })
                                }
                            >
                                <option value="">
                                    {t('app.ai_suggestions.add', {
                                        property: property.label,
                                    })}
                                </option>
                                {property.values.map((value) => (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </div>
                )
            })}
        </div>
    )
}

export default _AiSuggestions
